import { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/apiClient';
import type { HealthStatus, HealthDependencies, DatabaseHealth } from '../types';

interface UseHealthChecksReturn {
  healthStatus: HealthStatus | null;
  healthDependencies: HealthDependencies | null;
  databaseHealth: DatabaseHealth | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
  refreshAll: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  refreshDependencies: () => Promise<void>;
  refreshDatabase: () => Promise<void>;
  clearError: () => void;
}

export const useHealthChecks = (): UseHealthChecksReturn => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [healthDependencies, setHealthDependencies] = useState<HealthDependencies | null>(null);
  const [databaseHealth, setDatabaseHealth] = useState<DatabaseHealth | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      setError(null);
      const status = await apiClient.getHealthStatus();
      setHealthStatus(status);
      setLastUpdated(new Date().toISOString());
    } catch (error: any) {
      console.error('Failed to fetch health status:', error);
      setError(`Health status check failed: ${error.message || 'Unknown error'}`);
    }
  }, []);

  const refreshDependencies = useCallback(async () => {
    try {
      setError(null);
      const dependencies = await apiClient.getHealthDependencies();
      setHealthDependencies(dependencies);
      setLastUpdated(new Date().toISOString());
    } catch (error: any) {
      console.error('Failed to fetch health dependencies:', error);
      setError(`Dependencies check failed: ${error.message || 'Unknown error'}`);
    }
  }, []);

  const refreshDatabase = useCallback(async () => {
    try {
      setError(null);
      const database = await apiClient.getDatabaseHealth();
      setDatabaseHealth(database);
      setLastUpdated(new Date().toISOString());
    } catch (error: any) {
      console.error('Failed to fetch database health:', error);
      setError(`Database check failed: ${error.message || 'Unknown error'}`);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Run all health checks in parallel
      const [status, dependencies, database] = await Promise.allSettled([
        apiClient.getHealthStatus(),
        apiClient.getHealthDependencies(),
        apiClient.getDatabaseHealth()
      ]);

      // Process results
      if (status.status === 'fulfilled') {
        setHealthStatus(status.value);
      } else {
        console.error('Health status failed:', status.reason);
      }

      if (dependencies.status === 'fulfilled') {
        setHealthDependencies(dependencies.value);
      } else {
        console.error('Health dependencies failed:', dependencies.reason);
      }

      if (database.status === 'fulfilled') {
        setDatabaseHealth(database.value);
      } else {
        console.error('Database health failed:', database.reason);
      }

      setLastUpdated(new Date().toISOString());

      // Set error message if any checks failed
      const failedChecks = [status, dependencies, database]
        .filter(result => result.status === 'rejected')
        .length;

      if (failedChecks === 3) {
        setError('Unable to connect to API server. All health checks failed.');
      } else if (failedChecks > 0) {
        setError(`Some health checks failed (${failedChecks} of 3). Check individual services.`);
      }

    } catch (error: any) {
      console.error('Health checks failed:', error);
      setError(`Health checks failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load health checks on mount
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  return {
    healthStatus,
    healthDependencies,
    databaseHealth,
    isLoading,
    error,
    lastUpdated,
    refreshAll,
    refreshStatus,
    refreshDependencies,
    refreshDatabase,
    clearError
  };
};
